import { eventChannel } from 'redux-saga';
import { take, takeEvery, delay, put, putResolve, select, getContext, call, race } from 'redux-saga/effects';

import { getLogger } from '../../logger/index';
import {
  sendMessage,
  CLOSE_CUSTOMER_CHAT,
  closeCustomerChatFailure,
  closeCustomerChatSuccess,
  closeCustomerChatPending,
  openChat,
  endChatSocket,
  SEND_WELCOME_MESSAGE,
  setCustomerAgent,
  checkCustomerAgent,
  checkCustomerAgentDone,
  openConversation,
} from './actions';
import selectors from './selectors';
import authSelectors from '../auth/selectors';
import intlSelectors from '../intl/selectors';
import appSelectors from '../app/selectors';
import * as constants from '../../constants/index';
import auth from '../auth/index';
import intl from '../intl/index';
import { contact } from '../communication/entities';
import { setCustomerChat } from '../communication/actions';

const logger = getLogger('chat :: customer');

const SHORT_INTERVAL = 60 * 1000; // One minute
const LONG_INTERVAL = 5 * 60 * 1000; // Five minutes

function* sendWelcomeMessageSaga({ payload }) {
  try {
    logger.debug('Wait 1s to sent the welcome message');
    yield delay(1000);

    const messages = yield select(intlSelectors.getMessages);
    const authFirstName = yield select(authSelectors.getFirstName);
    const authLastName = yield select(authSelectors.getLastName);
    const authCompany = yield select(appSelectors.getProjectName);

    const rawDefaultMessage = yield select(
      appSelectors.getPluginSettingsValue(constants.plugin.CHEDRI_COMMS_CENTER, 'defaultMessage')
    );

    const mapObj = {
      '%firstName%': authFirstName,
      '%lastName%': authLastName,
      '%company%': authCompany,
    };

    const defaultMessage = (rawDefaultMessage || messages.chatter_welcome_message).replace(
      new RegExp(Object.keys(mapObj).join('|'), 'gi'),
      matched => {
        return mapObj[matched];
      }
    );

    logger.debug('send default welcome message to the customer after delay', payload.username, defaultMessage);
    yield put(
      openConversation(payload.username, payload.isAnonymousUser, payload.customerChat, payload.customerVideoCall)
    );
    yield delay(1000);
    yield put(sendMessage(payload.username, defaultMessage, payload));
  } catch (e) {
    logger.error(e);
  }
}

function* closeCustomerChatSaga({ payload: username }) {
  try {
    logger.debug('Send request to close customer chat');

    const activeConversations = yield select(selectors.getActiveConversations);
    const conversation = activeConversations.find(conv => conv.username === username);
    if (!conversation) {
      logger.warn('No conversation found with', username);
      return;
    }
    logger.debug('deactivate customer chat');
    yield call(deactivateCustomerChatSaga, { customerChatId: conversation.customerChatId, username });

    const authId = yield select(authSelectors.getId);
    logger.debug('Send end chat socket event');
    yield put(
      endChatSocket({
        from: authId,
        to: conversation.username,
        customerChatId: conversation.customerChatId,
        customerVideoChatId: conversation.customerVideoCallId,
        isAnonymousUser: conversation.isAnonymousUser,
      })
    );
  } catch (e) {
    logger.error(e);
  }
}

function* deactivateCustomerChatSaga({ customerChatId, username }) {
  let response;
  try {
    response = yield call(updateCustomerChatSaga, { customerChatId, username }, { isActive: false });
  } catch (e) {
    logger.error(e);
  }

  return response;
}

function* updateCustomerChatSaga({ customerChatId, username }, data) {
  let response;
  try {
    const http = yield getContext('http');
    const url = `/api/customerchats/${customerChatId}.json`;

    logger.debug('Request for updateCustomerChat', url, data);
    yield put(closeCustomerChatPending(username));
    response = yield call(http.put, url, data);

    if (response.status < 200 || response.status > 299) {
      logger.error('Error response for updateCustomerChat', response.status, response);
      yield put(closeCustomerChatFailure(username));
    } else {
      logger.debug('Success response for updateCustomerChat', response);
      yield put(closeCustomerChatSuccess(username));
    }
  } catch (e) {
    logger.error(e);
  }

  return response ? response.data : null;
}

function* fetchAgent() {
  const http = yield getContext('http');
  const locale = yield select(intl.selectors.getLocale);
  const requestUrl = `/api/chatteragent.json?&_locale=${locale}`;
  const requestConfig = {};

  const response = yield call(http.get, requestUrl, requestConfig);
  if (response.status < 200 || response.status > 299) {
    logger.error('Response with HTTP status', response.status, response);
    return null;
  }
  return response.data;
}

function* updateAgentStatus(agentData) {
  const currentAgent = yield select(selectors.getAgent);
  const contacts = yield select(contact.selectors.getList);
  const activeContact = yield select(selectors.getActiveContact);
  let newInterval = null;
  if (!activeContact) {
    if ((!agentData && currentAgent) || (currentAgent && currentAgent.id !== agentData.id)) {
      yield putResolve(contact.removeListItem(currentAgent.id));
    }
    if (agentData) {
      const containsAgent = contacts.some(({ id }) => id === agentData.id);
      if (!containsAgent) {
        yield putResolve(contact.addListItem(transformAgentToContact(agentData)));
        newInterval = LONG_INTERVAL;
      }
    } else {
      newInterval = SHORT_INTERVAL;
    }
    yield put(setCustomerAgent(agentData));
  } else {
    newInterval = LONG_INTERVAL;
  }
  return newInterval;
}

function createInterval(interval, emit) {
  return setInterval(() => {
    emit(true);
  }, interval || SHORT_INTERVAL);
}

function* checkForAgentsOnce() {
  try {
    logger.debug('checkForAgentsOnce');
    const agentData = yield fetchAgent();
    yield updateAgentStatus(agentData);
  } catch (err) {
    logger.error('Failed checking agent status', err);
  } finally {
    yield put(checkCustomerAgentDone());
  }
}

function* checkForAgents() {
  let handle;
  let emitter;
  let interval = SHORT_INTERVAL;
  const intervalChannel = eventChannel(emit => {
    emitter = emit;
    handle = createInterval(interval, emitter);
    return () => {
      clearInterval(handle);
    };
  });
  yield delay(3000);
  let firstCheck = false;
  while (true) {
    const currentAgent = yield select(selectors.getAgent);
    const [action] = firstCheck ? yield race([take('FETCH_CREDENTIALS_FULFILLED'), take(intervalChannel)]) : [];
    const isAuthenticated = yield select(auth.selectors.isAuthenticated);
    const isEmployee = yield select(auth.selectors.isEmployee);

    if (!isEmployee && !action) {
      try {
        const agentData = yield fetchAgent();
        if (agentData != null) {
          const newInterval = yield updateAgentStatus(agentData);
          if (newInterval != null && newInterval !== interval) {
            clearInterval(handle);
            handle = null;
            interval = newInterval;
          }
        }
      } catch (e) {
        logger.error(e);
      } finally {
        if (action) {
          yield put(checkCustomerAgentDone());
        }
        firstCheck = true;
      }
    } else if (isAuthenticated && currentAgent != null) {
      yield put(setCustomerAgent(null));
      if (interval !== LONG_INTERVAL) {
        clearInterval(handle);
        interval = LONG_INTERVAL;
        handle = null;
      }
    }
    if (handle == null) {
      handle = createInterval(interval, emitter);
    }
  }
}

function transformAgentToContact(agentData) {
  return {
    id: agentData.id,
    first_name: agentData.first_name,
    last_name: agentData.last_name,
    is_audio_call_available: agentData.is_audio_call_available,
    is_video_call_available: agentData.is_video_call_available,
    name: `${agentData.first_name} ${agentData.last_name}`,
    role: 'employee',
    is_anonymous_user: false,
    customer_chat_id: undefined,
    customer_video_call_id: undefined,
    is_mobile_online: false,
    is_online: true,
    last_seen: new Date(),
    unseen_messages: 0,
  };
}

export default function* customerSaga() {
  logger.debug('Watch new customer action');
  yield takeEvery(SEND_WELCOME_MESSAGE, sendWelcomeMessageSaga);
  yield takeEvery(CLOSE_CUSTOMER_CHAT, closeCustomerChatSaga);
  yield takeEvery(checkCustomerAgent, checkForAgentsOnce);
  yield race({
    watch: call(checkForAgents),
    cancel: take(action => action.type === 'SOCKET_EVENT' && action.payload.eventName === 'chat_ended'),
  });
}
